// This file based on the React Example provided by Floating UI documentation
// https://floating-ui.com/docs/popover
// https://codesandbox.io/p/sandbox/distracted-swirles-jo1pvu

import {
   arrow,
   autoUpdate,
   FloatingArrow,
   FloatingFocusManager,
   FloatingPortal,
   flip,
   offset,
   type Placement,
   shift,
   useClick,
   useDismiss,
   useFloating,
   useId,
   useInteractions,
   useMergeRefs,
   useRole
} from "@floating-ui/react";
import { cn } from "@tine/designsystem-utils";
import {
   type ButtonHTMLAttributes,
   cloneElement,
   createContext,
   type Dispatch,
   forwardRef,
   type HTMLProps,
   isValidElement,
   type PropsWithChildren,
   type ReactElement,
   type ReactNode,
   type Ref,
   type SetStateAction,
   useContext,
   useLayoutEffect,
   useMemo,
   useRef,
   useState
} from "react";

const ARROW_HEIGHT = 7;
const GAP = 2;

interface PopoverOptions {
   initialOpen?: boolean;
   placement?: Placement;
   modal?: boolean;
   open?: boolean;
   onOpenChange?: (open: boolean) => void;
   arrowClassName?: string;
   noFlip?: boolean;
}

export function usePopover({
   initialOpen = false,
   placement = "bottom",
   modal,
   open: controlledOpen,
   onOpenChange: setControlledOpen,
   noFlip = false
}: PopoverOptions = {}) {
   const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
   const [labelId, setLabelId] = useState<string | undefined>();
   const [descriptionId, setDescriptionId] = useState<string | undefined>();
   const arrowRef = useRef<SVGSVGElement>(null);
   const open = controlledOpen ?? uncontrolledOpen;
   const setOpen = setControlledOpen ?? setUncontrolledOpen;
   const middleware = [offset(ARROW_HEIGHT + GAP)];
   if (!noFlip) {
      middleware.push(
         flip({
            crossAxis: placement.includes("-"),
            fallbackAxisSideDirection: "end",
            padding: 5
         })
      );
      middleware.push(shift({ padding: 5 }));
      middleware.push(
         arrow({
            element: arrowRef
         })
      );
   }

   const data = useFloating({
      placement,
      open,
      onOpenChange: setOpen,
      whileElementsMounted: autoUpdate,
      middleware
   });

   const context = data.context;

   const click = useClick(context, {
      enabled: controlledOpen == null
   });
   const dismiss = useDismiss(context);
   const role = useRole(context);

   const interactions = useInteractions([click, dismiss, role]);

   return useMemo(
      () => ({
         open,
         setOpen,
         ...interactions,
         ...data,
         modal,
         labelId,
         descriptionId,
         setLabelId,
         setDescriptionId,
         arrowRef
      }),
      [open, setOpen, interactions, data, modal, labelId, descriptionId, arrowRef]
   );
}

type ContextType =
   | (ReturnType<typeof usePopover> & {
        setLabelId: Dispatch<SetStateAction<string | undefined>>;
        setDescriptionId: Dispatch<SetStateAction<string | undefined>>;
     })
   | null;

const PopoverContext = createContext<ContextType>(null);

export const usePopoverContext = () => {
   const context = useContext(PopoverContext);

   if (context == null) {
      throw new Error("Popover components must be wrapped in <Popover />");
   }

   return context;
};

export function Popover({
   children,
   modal = false,
   ...restOptions
}: {
   children: ReactNode;
} & PopoverOptions) {
   // This can accept any props as options, e.g. `placement`,
   // or other positioning options.
   const popover = usePopover({ modal, ...restOptions });
   return <PopoverContext.Provider value={popover}>{children}</PopoverContext.Provider>;
}

interface PopoverTriggerProps {
   children: ReactElement<PropsWithChildren> & { ref?: Ref<HTMLElement> };
   asChild?: boolean;
}

// Helper to safely extract an existing ref from a child element without using 'as any'.
function getExistingChildRef<E extends HTMLElement>(child: ReactElement): Ref<E> | undefined {
   const propsUnknown: unknown = child.props;
   if (propsUnknown && typeof propsUnknown === "object" && "ref" in (propsUnknown as object)) {
      const propsWithMaybeRef = propsUnknown as { ref?: Ref<E> };
      return propsWithMaybeRef.ref;
   }
   return undefined;
}

export const PopoverTrigger = forwardRef<HTMLElement, HTMLProps<HTMLElement> & PopoverTriggerProps>(function PopoverTrigger(
   { children, asChild = false, ...props },
   propRef
) {
   const context = usePopoverContext();
   let childRef: Ref<HTMLElement> | undefined;
   if (asChild && isValidElement(children)) {
      childRef = getExistingChildRef(children);
   }
   const ref = useMergeRefs([context.refs.setReference, propRef, childRef]);

   // `asChild` allows the user to pass any element as the anchor
   if (asChild && isValidElement(children)) {
      return cloneElement(
         children,
         context.getReferenceProps({
            ref,
            ...props,
            ...(typeof children.props === "object" ? children.props : {}),
            "data-state": context.open ? "open" : "closed"
         } as HTMLProps<HTMLElement>)
      );
   }

   // The user can style the trigger based on the state using this data attribute
   return (
      <button ref={ref} type="button" data-state={context.open ? "open" : "closed"} {...context.getReferenceProps(props)}>
         {children}
      </button>
   );
});

export const PopoverContent = forwardRef<HTMLDivElement, HTMLProps<HTMLDivElement>>(function PopoverContent(
   { style, ...props },
   propRef
) {
   const { context: floatingContext, ...context } = usePopoverContext();
   const ref = useMergeRefs([context.refs.setFloating, propRef]);

   if (!floatingContext.open) return null;

   const arrowClasses = cn("fill-base-0 [&>path:first-of-type]:stroke-ink-border-subtle [&>path:last-of-type]:stroke-base-0");

   const floatingClasses = cn(
      "bg-base-0 border border-solid border-ink-border-subtle rounded-lg shadow-lg z-50",
      props.className || ""
   );

   return (
      <FloatingPortal>
         <FloatingFocusManager context={floatingContext} modal={context.modal}>
            {/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: role is generated by floating-ui */}
            <div
               ref={ref}
               style={{ ...context.floatingStyles, ...style }}
               aria-labelledby={context.labelId}
               aria-describedby={context.descriptionId}
               data-info="popover"
               {...context.getFloatingProps(props)}
               className={floatingClasses}
            >
               {props.children}
               <FloatingArrow context={floatingContext} ref={context.arrowRef} className={arrowClasses} strokeWidth={1} />
            </div>
         </FloatingFocusManager>
      </FloatingPortal>
   );
});

export const PopoverHeading = forwardRef<HTMLHeadingElement, HTMLProps<HTMLHeadingElement>>(function PopoverHeading(props, ref) {
   const { setLabelId } = usePopoverContext();
   const id = useId();

   // Only sets `aria-labelledby` on the Popover root element
   // if this component is mounted inside it.
   useLayoutEffect(() => {
      setLabelId(id);
      return () => setLabelId(undefined);
   }, [id, setLabelId]);

   return (
      <h2 {...props} ref={ref} id={id}>
         {props.children}
      </h2>
   );
});

export const PopoverDescription = forwardRef<HTMLParagraphElement, HTMLProps<HTMLParagraphElement>>(
   function PopoverDescription(props, ref) {
      const { setDescriptionId } = usePopoverContext();
      const id = useId();

      // Only sets `aria-describedby` on the Popover root element
      // if this component is mounted inside it.
      useLayoutEffect(() => {
         setDescriptionId(id);
         return () => setDescriptionId(undefined);
      }, [id, setDescriptionId]);

      return <p {...props} ref={ref} id={id} />;
   }
);

export const PopoverClose = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
   function PopoverClose(props, ref) {
      const { setOpen } = usePopoverContext();
      return (
         <button
            type="button"
            ref={ref}
            {...props}
            onClick={(event) => {
               props.onClick?.(event);
               setOpen(false);
            }}
         />
      );
   }
);
